import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Bell, CheckCircle, AlertCircle, Info, Trash2, Check, Filter } from "lucide-react"
import { Button, Card, CardBody, Chip, Select, SelectItem, Checkbox } from "@heroui/react"

interface Notification {
    id: string
    type: "success" | "warning" | "info" | "error"
    title: string
    message: string
    timestamp: string
    isRead: boolean
    category: "system" | "task" | "pasco" | "user"
}

export default function NotificationsPage() {
    const navigate = useNavigate()
    const [filterCategory, setFilterCategory] = useState<string>("all")
    const [filterStatus, setFilterStatus] = useState<string>("all")

    // Mock notifications data
    const [notifications, setNotifications] = useState<Notification[]>([
        {
            id: "1",
            type: "success",
            title: "Task Completed",
            message: "PASCO processing for CS101 has been completed successfully",
            timestamp: "5 minutes ago",
            isRead: false,
            category: "task",
        },
        {
            id: "2",
            type: "error",
            title: "Task Failed",
            message: "PASCO processing for MATH201 failed. Error: File format not supported",
            timestamp: "1 hour ago",
            isRead: false,
            category: "task",
        },
        {
            id: "3",
            type: "warning",
            title: "Pending Approval",
            message: "3 new PASCOs are waiting for your approval",
            timestamp: "2 hours ago",
            isRead: true,
            category: "pasco",
        },
        {
            id: "4",
            type: "info",
            title: "System Update",
            message: "System maintenance scheduled for tonight at 11:00 PM",
            timestamp: "3 hours ago",
            isRead: true,
            category: "system",
        },
        {
            id: "5",
            type: "success",
            title: "New User Added",
            message: "John Doe has been added as an administrator",
            timestamp: "5 hours ago",
            isRead: true,
            category: "user",
        },
    ])

    const filteredNotifications = notifications.filter((notif) => {
        const matchesCategory = filterCategory === "all" || notif.category === filterCategory
        const matchesStatus =
            filterStatus === "all" ||
            (filterStatus === "unread" && !notif.isRead) ||
            (filterStatus === "read" && notif.isRead)
        return matchesCategory && matchesStatus
    })

    const unreadCount = notifications.filter(n => !n.isRead).length

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case "success": return <CheckCircle className="h-5 w-5 text-success" />
            case "error": return <AlertCircle className="h-5 w-5 text-danger" />
            case "warning": return <AlertCircle className="h-5 w-5 text-warning" />
            default: return <Info className="h-5 w-5 text-primary" />
        }
    }

    const getNotificationColor = (type: string) => {
        switch (type) {
            case "success": return "success"
            case "error": return "danger"
            case "warning": return "warning"
            default: return "primary"
        }
    }

    const markAsRead = (id: string) => {
        setNotifications(notifications.map(n =>
            n.id === id ? { ...n, isRead: true } : n
        ))
    }

    const markAllAsRead = () => {
        setNotifications(notifications.map(n => ({ ...n, isRead: true })))
    }

    const deleteNotification = (id: string) => {
        setNotifications(notifications.filter(n => n.id !== id))
    }

    const deleteAllRead = () => {
        if (window.confirm("Delete all read notifications?")) {
            setNotifications(notifications.filter(n => !n.isRead))
        }
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row min-h-[56px] sm:h-16 py-2 sm:py-0 items-start sm:items-center justify-between gap-2 sm:gap-0">
                        <div className="flex items-center gap-3">
                            <Button
                                isIconOnly
                                variant="light"
                                onPress={() => navigate(-1)}
                                className="flex-shrink-0"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                                <Bell className="h-5 w-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-xl font-semibold text-foreground">Notifications</h1>
                            {unreadCount > 0 && (
                                <Chip color="danger" size="sm" variant="flat">
                                    {unreadCount} unread
                                </Chip>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                            {unreadCount > 0 && (
                                <Button
                                    color="primary"
                                    variant="flat"
                                    size="sm"
                                    onPress={markAllAsRead}
                                    startContent={<Check className="h-4 w-4" />}
                                    className="rounded flex-1 sm:flex-none"
                                >
                                    <span className="hidden sm:inline">Mark all as read</span>
                                    <span className="sm:hidden">Mark Read</span>
                                </Button>
                            )}
                            <Button
                                color="danger"
                                variant="flat"
                                size="sm"
                                onPress={deleteAllRead}
                                startContent={<Trash2 className="h-4 w-4" />}
                                className="rounded flex-1 sm:flex-none"
                            >
                                <span className="hidden sm:inline">Clear read</span>
                                <span className="sm:hidden">Clear</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Filters */}
                    <Card>
                        <CardBody className="p-4">
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Filter className="h-4 w-4 text-default-400" />
                                    <span className="text-sm text-default-500">Filters:</span>
                                </div>
                                <Select
                                    label="Category"
                                    placeholder="All categories"
                                    selectedKeys={[filterCategory]}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                    size="sm"
                                    className="w-40"
                                    classNames={{ trigger: "h-10" }}
                                >
                                    <SelectItem key="all">All Categories</SelectItem>
                                    <SelectItem key="system">System</SelectItem>
                                    <SelectItem key="task">Tasks</SelectItem>
                                    <SelectItem key="pasco">PASCOs</SelectItem>
                                    <SelectItem key="user">Users</SelectItem>
                                </Select>
                                <Select
                                    label="Status"
                                    placeholder="All status"
                                    selectedKeys={[filterStatus]}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    size="sm"
                                    className="w-40"
                                    classNames={{ trigger: "h-10" }}
                                >
                                    <SelectItem key="all">All</SelectItem>
                                    <SelectItem key="unread">Unread</SelectItem>
                                    <SelectItem key="read">Read</SelectItem>
                                </Select>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Notifications List */}
                    {filteredNotifications.length === 0 ? (
                        <Card>
                            <CardBody className="flex flex-col items-center justify-center p-12">
                                <Bell className="h-12 w-12 text-default-300 mb-3" />
                                <p className="text-lg font-semibold text-default-500">No notifications</p>
                                <p className="text-sm text-default-400 mt-1">You're all caught up!</p>
                            </CardBody>
                        </Card>
                    ) : (
                        <div className="space-y-3">
                            {filteredNotifications.map((notification) => (
                                <Card
                                    key={notification.id}
                                    className={`${!notification.isRead ? 'border-l-4 border-l-primary bg-primary/5' : ''}`}
                                >
                                    <CardBody className="p-4">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2 mb-1">
                                                    <h4 className="font-semibold text-foreground">
                                                        {notification.title}
                                                    </h4>
                                                    <div className="flex items-center gap-2">
                                                        <Chip
                                                            size="sm"
                                                            variant="flat"
                                                            color={getNotificationColor(notification.type) as any}
                                                        >
                                                            {notification.category}
                                                        </Chip>
                                                        {!notification.isRead && (
                                                            <div className="w-2 h-2 rounded-full bg-primary" />
                                                        )}
                                                    </div>
                                                </div>
                                                <p className="text-sm text-default-600 mb-2">
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-default-400">
                                                        {notification.timestamp}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        {!notification.isRead && (
                                                            <Checkbox
                                                                size="sm"
                                                                isSelected={notification.isRead}
                                                                onValueChange={() => markAsRead(notification.id)}
                                                            >
                                                                <span className="text-xs">Mark as read</span>
                                                            </Checkbox>
                                                        )}
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            color="danger"
                                                            onPress={() => deleteNotification(notification.id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    )
}
